import { useEffect, useState } from 'react'
import { supabase, getFileUrl } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { FileText, FileImage, FileSpreadsheet, File, Download, Trash2, RefreshCw } from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

function FileIcon({ mime }) {
  if (!mime) return <File size={18} className="text-gray-400" />
  if (mime.includes('pdf')) return <FileText size={18} className="text-red-500" />
  if (mime.includes('image')) return <FileImage size={18} className="text-blue-500" />
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return <FileSpreadsheet size={18} className="text-green-500" />
  if (mime.includes('word') || mime.includes('document')) return <FileText size={18} className="text-brand-500" />
  return <File size={18} className="text-gray-400" />
}

export default function FileList({ entityType, entityId, refreshKey }) {
  const { isReadOnly } = useAuth()
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchFiles() }, [entityId, refreshKey])

  async function fetchFiles() {
    setLoading(true)
    const { data } = await supabase
      .from('files')
      .select('*, uploader:profiles!files_uploaded_by_fkey(full_name)')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false })
    setFiles(data ?? [])
    setLoading(false)
  }

  async function handleDownload(file) {
    const url = await getFileUrl(file.storage_path)
    if (url) window.open(url, '_blank')
  }

  async function handleDelete(file) {
    if (!confirm(`¿Eliminar "${file.file_name}"?`)) return
    await supabase.storage.from('consulting-files').remove([file.storage_path])
    await supabase.from('files').delete().eq('id', file.id)
    setFiles(f => f.filter(x => x.id !== file.id))
  }

  if (loading) return <div className="text-center py-6 text-gray-400 text-sm">Cargando archivos...</div>
  if (files.length === 0) return <div className="text-center py-6 text-gray-400 text-sm">No hay archivos subidos aún</div>

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">{files.length} archivo{files.length !== 1 ? 's' : ''}</h3>
        <button onClick={fetchFiles} className="text-gray-400 hover:text-gray-600"><RefreshCw size={14} /></button>
      </div>
      {files.map(file => (
        <div key={file.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
          <FileIcon mime={file.mime_type} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{file.file_name}</p>
            <p className="text-xs text-gray-400">
              {file.uploader?.full_name} · {format(new Date(file.created_at), 'd MMM yyyy', { locale: es })}
              {file.file_size && ` · ${(file.file_size / 1024 / 1024).toFixed(2)} MB`}
            </p>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => handleDownload(file)} className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors" title="Descargar">
              <Download size={14} />
            </button>
            {!isReadOnly && (
              <button onClick={() => handleDelete(file)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
